import PropTypes from "prop-types";

// @mui material components
import Icon from "@mui/material/Icon";

// Material Dashboard 2 React components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDAvatar from "components/MDAvatar";

// Timeline context
import { useTimeline } from "examples/Timeline/context";

// Custom styles for the TimelineItem
import timelineItem from "examples/Timeline/TimelineItem/styles";
import calenderIcon from "../../../assets/images/icons/catering-icons/calenderIcon.svg";
import pointsIcon from "../../../assets/images/icons/catering-icons/pointsIcon.svg";
import dollarIcon from "../../../assets/images/icons/catering-icons/dollarBag.svg";
import { ToolTipUI } from "examples/Widgets/ToolTipUI";
import formatNumberWithCommas from "../../../core/utils/formatNumberWithCommas";

function TimelineItem({
  color,
  icon,
  title,
  dateTime,
  description,
  lastItem,
  orderPoint,
  orderAmount,
  campaignPoint,
  nsuPoints,
  rewardPoints,
  isIconRequired,
  campaignName,
}) {
  const isDark = useTimeline();

  return (
    <MDBox position="relative" mb={3} sx={(theme) => timelineItem(theme, { lastItem, isDark })}>
      <MDBox
        display="flex"
        justifyContent="center"
        alignItems="center"
        color="white"
        width="2rem"
        height="2rem"
        position="absolute"
        top="8%"
        left="2px"
        zIndex={2}
        sx={{ fontSize: ({ typography: { size } }) => size.sm }}
      >
        <MDAvatar
          src={icon}
          name={"orderOverview"}
          size={isIconRequired ? "sm" : "md"}
          sx={{ borderRadius: "0px" }}
        />
      </MDBox>
      <MDBox ml={5.75} pt={description ? 0.7 : 0.5} lineHeight={0} maxWidth="30rem">
        <MDTypography variant="button" fontWeight="medium" color={isDark ? "white" : "dark"}>
          {title}
        </MDTypography>
        <MDBox mt={0.5} display="flex" alignItems="center" gap={1}>
          {isIconRequired && <MDAvatar src={calenderIcon} name={"calenderIcon"} size="xs" />}
          <MDTypography variant="caption" color={isDark ? "secondary" : "text"}>
            {dateTime}
          </MDTypography>
        </MDBox>
        <MDBox mt={0.5} display="flex" alignItems="center" gap={1} sx={{ flexFlow: "wrap" }}>
          {isIconRequired && <MDAvatar src={pointsIcon} name="pointsIcon" size="xs" />}

          {[
            { value: orderPoint, tooltip: null, ml: 0 },
            {
              value: campaignPoint,
              tooltip: `Extra points earned through the campaign "${campaignName}"`,
              ml: -2,
            },
            { value: nsuPoints, tooltip: "Sign up bonus Points", ml: -3 },
            { value: rewardPoints, tooltip: "Custom Reward Points", ml: -3 },
          ]
            .filter((item) => Number(item.value) > 0)
            .map((item, index) => (
              <MDTypography
                key={index}
                variant="caption"
                color={isDark ? "secondary" : "text"}
                display="flex"
                sx={{ ml: `${item.ml}px` }}
              >
                {index > 0 && "+ "}
                {formatNumberWithCommas(item.value)}&nbsp;
                {item.tooltip && <ToolTipUI tooltipText={item.tooltip} />}
              </MDTypography>
            ))}

          {/* Show final "points" label only if there is at least one point */}
          {(orderPoint || campaignPoint || nsuPoints || rewardPoints) && (
            <MDTypography
              variant="caption"
              color={isDark ? "secondary" : "text"}
              sx={{ ml: "-6px" }}
            >
              points
            </MDTypography>
          )}
        </MDBox>
      </MDBox>
      <MDBox mt={0.5} display="flex" alignItems="center" gap={1}>
        {isIconRequired && <MDAvatar src={dollarIcon} name={"dollarIcon"} size="xs" />}
        <MDTypography variant="caption" color={isDark ? "white" : "dark"}>
          {orderAmount}
        </MDTypography>
      </MDBox>
    </MDBox>
  );
}

// Setting default values for the props of TimelineItem
TimelineItem.defaultProps = {
  color: "info",
  lastItem: false,
  description: "",
};

// Typechecking props for the TimelineItem
TimelineItem.propTypes = {
  color: PropTypes.oneOf([
    "primary",
    "secondary",
    "info",
    "success",
    "warning",
    "error",
    "dark",
    "light",
  ]),
  icon: PropTypes.node.isRequired,
  title: PropTypes.string.isRequired,
  dateTime: PropTypes.string.isRequired,
  description: PropTypes.string,
  lastItem: PropTypes.bool,
  orderPoint: PropTypes.string,
  orderAmount: PropTypes.string,
  campaignPoint: PropTypes.string,
  nsuPoints: PropTypes.string,
  rewardPoints: PropTypes.string,
  isIconRequired: PropTypes.bool,
  campaignName: PropTypes.string,
};

export default TimelineItem;
